import { useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Phone, Mail, Trash2, ChevronDown, ChevronUp } from "lucide-react";

export const LEAD_STATUSES = ["new", "contacted", "booked", "closed"];
const STATUS_CLS = {
  new: "bg-[#FBF2E1] text-[#8A6529]",
  contacted: "bg-[#E8F1FD] text-[#1D4E89]",
  booked: "bg-[#E5F7ED] text-[#087443]",
  closed: "bg-[#F1F2F4] text-[#51565F]",
};
const SOURCE_LABEL = { homepage_lead: "Homepage form", contact: "Contact page" };

export function LeadsTab({ leads, counts, onChanged }) {
  const [filter, setFilter] = useState("all");
  const shown = leads.filter((l) => filter === "all" || l.status === filter);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {["all", ...LEAD_STATUSES].map((s) => (
          <button key={s} data-testid={`leads-filter-${s}`} onClick={() => setFilter(s)}
            className={`px-4 py-2 rounded-xl text-[11px] font-mono uppercase tracking-[0.1em] transition-colors ${filter === s ? "bg-[#0C2347] text-white" : "bg-white border border-[#E4E9F0] text-[#475467] hover:border-[#0C2347]/30"}`}>
            {s} <span className="opacity-60 ml-1">{s === "all" ? leads.length : counts?.[s] ?? 0}</span>
          </button>
        ))}
      </div>
      <div className="rounded-[14px] border border-[#E4E9F0] bg-white divide-y divide-[#F1F2F4] overflow-hidden" style={{ boxShadow: "0 3px 12px rgba(15,23,42,0.035)" }}>
        {shown.map((l) => <LeadRow key={l.id} lead={l} onChanged={onChanged} />)}
        {shown.length === 0 && <p data-testid="leads-empty" className="p-10 text-center text-[#94A3B8] text-sm">No leads here yet.</p>}
      </div>
    </div>
  );
}

function LeadRow({ lead, onChanged }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);

  const setStatus = async (status) => {
    try { await api.patch(`/admin/leads/${lead.id}`, { status }); onChanged(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const saveNotes = async () => {
    setSaving(true);
    try { await api.patch(`/admin/leads/${lead.id}`, { notes }); toast.success("Notes saved"); onChanged(); }
    catch { toast.error("Could not save notes"); }
    finally { setSaving(false); }
  };
  const remove = async () => { await api.delete(`/admin/leads/${lead.id}`); toast.success("Lead deleted"); onChanged(); };

  const when = new Date(lead.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  return (
    <div data-testid={`lead-${lead.id}`} className="p-4 md:p-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="min-w-[180px] flex-1">
          <div className="font-medium text-[#101828]">{lead.name}</div>
          <div className="text-xs text-[#94A3B8] font-mono mt-0.5">{when} · {SOURCE_LABEL[lead.source] || lead.source}</div>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <a href={`mailto:${lead.email}`} data-testid={`lead-email-${lead.id}`} className="inline-flex items-center gap-1.5 text-[#475467] hover:text-[#0C2347] underline-offset-4 hover:underline"><Mail className="h-3.5 w-3.5" strokeWidth={1.5} />{lead.email}</a>
          {lead.phone && <a href={`tel:${lead.phone}`} data-testid={`lead-phone-${lead.id}`} className="inline-flex items-center gap-1.5 text-[#475467] hover:text-[#0C2347] underline-offset-4 hover:underline"><Phone className="h-3.5 w-3.5" strokeWidth={1.5} />{lead.phone}</a>}
        </div>
        <select data-testid={`lead-status-${lead.id}`} value={lead.status || "new"} onChange={(e) => setStatus(e.target.value)}
          className={`h-8 px-2.5 rounded-full text-[11px] font-medium bg-transparent outline-none cursor-pointer ${STATUS_CLS[lead.status || "new"]}`}>
          {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Button variant="ghost" size="sm" data-testid={`lead-toggle-${lead.id}`} onClick={() => setOpen(!open)}>{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</Button>
      </div>
      {(lead.address || lead.message) && !open && (
        <div className="text-sm text-[#475467] mt-2 truncate">{lead.address || lead.message}</div>
      )}
      {open && (
        <div className="mt-4 grid md:grid-cols-2 gap-6">
          <div className="space-y-2 text-sm">
            {lead.address && <div><span className="text-[11px] font-mono uppercase tracking-[0.1em] text-[#475467] block">Property</span>{lead.address}</div>}
            {lead.message && <div><span className="text-[11px] font-mono uppercase tracking-[0.1em] text-[#475467] block">Message</span><p className="whitespace-pre-wrap">{lead.message}</p></div>}
            <Button variant="ghost" size="sm" data-testid={`lead-delete-${lead.id}`} onClick={remove} className="text-red-600 px-0 hover:bg-transparent"><Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />Delete lead</Button>
          </div>
          <div>
            <span className="text-[11px] font-mono uppercase tracking-[0.1em] text-[#475467] block mb-1">Notes</span>
            <Textarea data-testid={`lead-notes-${lead.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-xl border-[#E4E9F0]" placeholder="Call summary, follow-up date…" />
            <Button data-testid={`lead-notes-save-${lead.id}`} size="sm" disabled={saving} onClick={saveNotes} className="mt-2 rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white font-mono text-[11px] uppercase tracking-[0.1em]">{saving ? "Saving…" : "Save notes"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
